import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CSSTransition } from "react-transition-group";
import { Button, Message, Segment, Select } from "semantic-ui-react";
import { AddNPShift } from "../../../../../actions/npShiftsActions";
import { tableValuesInit } from "../../../../../models/npShifts";
import { AppState } from "../../../../../store/configureStore";
import { INPShift, npShiftsInit } from "../../../../../store/npShiftsReducer";
import { Loading } from "../../../../Loading/Loading";
import { getNpCoresAndNonCores, getRefProgs } from "./calculateNPValuesHelper";
import "./NPShift.css";
import { NPShiftTable } from "./NPShiftTable";
import { parseNPFile } from "./parseNPFileHelper";
import { ValidationProcess } from "../../../../../store/validationProcess";

const SAVED_MESSAGE_DURATION_MS = 2000;
const MESSAGE_TRANSITION_TIMEOUT = 400;
const messageTransitionProps = {
    timeout: MESSAGE_TRANSITION_TIMEOUT,
    classNames: "npshift-transition",
    unmountOnExit: true,
};

const checkNpShiftsSum = (npShiftsArray: INPShift) => {
    let sum = 0;
    for (let el in npShiftsArray.values) sum += parseFloat(npShiftsArray.values[el].value + "");
    return sum;
};

export const NPShift = () => {
    const dispatch = useDispatch();
    const zipFile = useSelector((state: AppState) => state.files.zipFile);
    const npShifts = useSelector((state: AppState) => state.npShifts.npShifts);
    const [selectedTimestamp, setSelectedTimestamp] = useState<number>(-1);
    const [selectedNPShift, setSelectedNPShift] = useState<INPShift>();
    const [loading, setLoading] = useState(false);
    const [showSumErrorMessage, setShowSumErrorMessage] = useState(false);
    const [showSavedMessage, setShowSavedMessage] = useState(false);
    const [refProgs, setRefProgs] = useState(tableValuesInit);
    const [hasExchangeData, setHasExchangeData] = useState(tableValuesInit);
    const [newTotals, setNewTotals] = useState(tableValuesInit);
    const [npCores, setNpCores] = useState(tableValuesInit);
    const [npNonCores, setNpNonCores] = useState(tableValuesInit);
    const activeTimestampsOptions = useSelector((state: AppState) => state.timestamps.timestamps);
    const availableTimestampsOrScenarios = useSelector(
        (state: AppState) => state.validationProcess.availableTimestampsOrScenarios
    );
    const validationProcess = useSelector((state: AppState) => state.validationProcess.process);
    const usedInitStateRef = useRef(false);
    const loadSucceededRef = useRef(false);

    const handleAddNPShift = () => {
        selectedNPShift!.hour = selectedTimestamp;
        dispatch(AddNPShift(selectedNPShift!));
        setShowSavedMessage(true);
        setTimeout(() => setShowSavedMessage(false), SAVED_MESSAGE_DURATION_MS);
    };

    const getNPValues = useCallback(async () => {
        let xmlFilePositionElementValue = (selectedTimestamp + 1).toString();
        if (validationProcess === ValidationProcess.Ltcc) {
            //Always 1 for LTCC files
            xmlFilePositionElementValue = "1";
        }
        else if (validationProcess === ValidationProcess.IntraDay && availableTimestampsOrScenarios.length === 18) {
            //ID fid3c-files; index for first TS 06:30 is 1; 18 timestamps total
            xmlFilePositionElementValue = ((selectedTimestamp + 1) - 6).toString();
        }
        else if (validationProcess === ValidationProcess.IntraDay && availableTimestampsOrScenarios.length === 12) {
            //ID fid3-files; index for first TS 12:30 is 1; 12 timestamps total
            xmlFilePositionElementValue = ((selectedTimestamp + 1) - 12).toString();
        }
        else if (validationProcess === ValidationProcess.IntraDay && availableTimestampsOrScenarios.length === 6) {
            //ID fid3e-files; index for first TS 18:30 is 1; 6 timestamps total
            xmlFilePositionElementValue = ((selectedTimestamp + 1) - 18).toString();
        }
        const selectedValue = activeTimestampsOptions.find(f => f.key === selectedTimestamp)?.text;
        const json = await parseNPFile(zipFile!, selectedValue);
        const refProgsResult = await getRefProgs(json, xmlFilePositionElementValue);
        setRefProgs({ ...refProgsResult.refProgs });
        setHasExchangeData({ ...refProgsResult.hasExchangeData });
        setNewTotals({ ...refProgsResult.refProgs });
        if (usedInitStateRef.current) {
            const hasExchange = refProgsResult.hasExchangeData;
            setSelectedNPShift(prev => {
                if (!prev) return prev;
                const nextValues = { ...prev.values };
                Object.keys(nextValues).forEach(country => {
                    if (hasExchange[country] !== 1) {
                        nextValues[country] = { ...nextValues[country], bznShift: 0 };
                    }
                });
                return { ...prev, values: nextValues };
            });
        }
        const newCoresAndNonCores = await getNpCoresAndNonCores(
            json,
            xmlFilePositionElementValue,
            refProgsResult.hasExchangeData
        );
        setNpCores({ ...newCoresAndNonCores.cores });
        setNpNonCores({ ...newCoresAndNonCores.nonCores });
        loadSucceededRef.current = true;
        setLoading(false);
    }, [selectedTimestamp, zipFile, activeTimestampsOptions, validationProcess, availableTimestampsOrScenarios.length]);

    useEffect(() => {
        if (selectedNPShift) {
            let sum = checkNpShiftsSum(selectedNPShift);
            if (isNaN(sum) || sum !== 0) setShowSumErrorMessage(true);
            else setShowSumErrorMessage(false);
        }
    }, [selectedNPShift]);

    useEffect(() => {
        if (zipFile && selectedTimestamp > -1) {
            loadSucceededRef.current = false;
            setLoading(true);
            const npShift = npShifts.filter(x => x.hour === selectedTimestamp)[0];
            if (npShift) {
                setSelectedNPShift(npShift);
                usedInitStateRef.current = false;
            } else {
                setSelectedNPShift(npShiftsInit);
                usedInitStateRef.current = true;
            }
            getNPValues().catch(error => {
                console.error(error);
                setLoading(false);
            });
        }
    }, [zipFile, selectedTimestamp, getNPValues, npShifts]);

    const showTable = selectedTimestamp !== -1 && activeTimestampsOptions.some(vendor => vendor.key === selectedTimestamp);
    const hasMissingMarketAreasInRefProg =
        showTable &&
        selectedNPShift &&
        Object.keys(selectedNPShift.values).some(country => hasExchangeData[country] !== 1);

    const sumNpShiftNonHighlighted =
        selectedNPShift &&
        Object.keys(selectedNPShift.values)
            .filter(country => hasExchangeData[country] === 1)
            .reduce((sum, country) => sum + (parseFloat(selectedNPShift!.values[country].value + "") || 0), 0);
    const isRefProgNotBalanced = (sumNpShiftNonHighlighted ?? 0) !== 0;

    const sumErrorMessage = (
        <Message id="message-NPShift-a" negative>
            <Message.Header id="messageHeader-NPShift-sumOfAll">Sum of all elements must be zero</Message.Header>
        </Message>
    );
    const savedMessage = (
        <Message id="message-NPShift-b" positive>
            <Message.Header id="messageHeader-NPShift-shiftsSaved">NP shifts saved</Message.Header>
        </Message>
    );

    return (
        <Segment id="segment-NPShift-a" className="np-shift-container transition-container">
            <div id="div-NPShift-a" className="np-shift-header">
                <div id="div-NPShift-b">
                    <p id="p-NPShift-timestamp">Timestamp</p>
                    <Select
                        id="select-NPShift-a"
                        selectOnBlur={false}
                        placeholder="Select a timestamp"
                        value={selectedTimestamp}
                        options={activeTimestampsOptions}
                        onChange={(e, data: any) => setSelectedTimestamp(+data.value)}
                    />
                    <Button
                        id="button-NPShift-a"
                        primary
                        content="Save NP shifts"
                        onClick={handleAddNPShift}
                        disabled={showSumErrorMessage || !selectedNPShift}
                    />
                </div>
                {!zipFile ? <Message>Please upload a file</Message> : null}
                <div className="np-shift-messages-column">
                    <CSSTransition id="CSSTranstion-NPShift-a" in={showSumErrorMessage} {...messageTransitionProps}>
                        {sumErrorMessage}
                    </CSSTransition>
                    <CSSTransition id="CSSTranstion-NPShift-b" in={showSavedMessage} {...messageTransitionProps}>
                        {savedMessage}
                    </CSSTransition>
                </div>
                <div className="np-shift-value-message-wrapper">
                    <Message id="message-NPShift-value">NP shift value &gt; 0 - Export | NP shift value &lt; 0 - Import</Message>
                    {!loading && hasMissingMarketAreasInRefProg && (
                        <Message id="message-NPShift-refProgMissing" className="npshift-refprog-not-balanced-message">
                            <Message.Header id="messageHeader-NPShift-refProgMissing">
                                One or more Market Areas are not present in the RefProg file
                            </Message.Header>
                            {isRefProgNotBalanced && (
                                <p id="messageContent-NPShift-refProgMissing">
                                    Market areas available in RefProg are not balanced.
                                </p>
                            )}
                        </Message>
                    )}
                </div>
            </div>
            {loading ? (
                <Loading />
            ) : loadSucceededRef.current && zipFile && showTable && selectedNPShift && (
                <NPShiftTable
                    selectedNPShift={selectedNPShift}
                    setSelectedNPShift={setSelectedNPShift}
                    newTotals={newTotals}
                    setNewTotals={setNewTotals}
                    refProgs={refProgs}
                    setRefProgs={setRefProgs}
                    hasExchangeData={hasExchangeData}
                    npCores={npCores}
                    setNpCores={setNpCores}
                    npNonCores={npNonCores}
                    setNpNonCores={setNpNonCores}
                />
            )}
        </Segment>
    );
};
